import { gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { NotificationStatus } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { ensure } from '~utils/types';
import { useResetNotificationSettingsMutation } from './__generated__/ResetNotificationModal.graphql';

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
  status?: NotificationStatus;
}
export function ResetNotificationModal({ isOpen, onCancel, status }: Props) {
  const toast = useToast();
  const { customerId } = useParams();
  const [resetNotificationSettings, { loading }] = useResetNotificationSettingsMutation({
    refetchQueries: ['EmailNotificationsPage'],
    awaitRefetchQueries: true,
  });

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleReset = useCallback(async () => {
    try {
      const { data: result } = await resetNotificationSettings({
        variables: {
          input: {
            customerId: ensure(customerId),
            status: status ?? NotificationStatus.Enabled,
          },
        },
      });

      if (!result?.notificationSettingsUpdate) {
        throw new Error(t('notificationUpdateFailed'));
      }

      toast({
        status: 'success',
        title: t('encResetSuccess', {}),
        description: t('encResetSuccessDesc', {}),
      });
      onCancel();
    } catch (err) {
      toast({
        status: 'error',
        title: t('updateNotificationError'),
        description: fromError(err, 'NotificationSettingsUpdate'),
      });
    }
  }, [resetNotificationSettings, customerId, status, toast, onCancel]);

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('resetToDefault')}</ModalHeader>
        <ModalCloseButton tabIndex={5} />
        <ModalBody>
          <Text>{t('resetDefaultDesc')}</Text>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} variant="ghost" colorScheme="blue" isDisabled={loading}>
            {t('cancel')}
          </Button>
          <Button
            variant="solid"
            colorScheme="red"
            marginLeft="3"
            onClick={handleReset}
            isDisabled={loading}
          >
            {t('reset')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

ResetNotificationModal.graphql = {
  mutations: {
    resetNotificationSettings: gql`
      mutation resetNotificationSettings($input: NotificationInput!) {
        notificationSettingsUpdate(input: $input) {
          id
        }
      }
    `,
  },
};
